import { faFileLines, faFileInvoice } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { ButtonSecondary } from '../../../../../../../../../../../baseComponents/Button'
import { ModalBigCentered } from '../../../../../../../../../../../baseComponents/Modal'
import { TableData } from '../../../../../../../../../../../baseComponents/TwinTable/types'
import TwinTrans from '../../../../../../../../../../../baseComponents/TwinTrans'
import twinFetchPost from '../../../../../../../../../../../utils/globals/data'
import locations from '../../../../../../../../../../../utils/globals/locations'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { getPreparePricesConfigParam, getUserPermissions } from '../../../../../../../../../../../utils/reducers/getters'
import { changeErrorMessage } from '../../../../../../../../../../../utils/reducers/reduxDispatch'
import { OrderData } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import { parseSelectedTableForProcessing, SelectedTableForProcessingTy } from '../../../../../functions'
import { ProcessButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'
import { checkSellDeliveryNoteFromOrderAllOk, checkSellInvoiceFromOrderAllOk } from '../../../../../../../../../../../utils/globals/order/sell'
import { permissionCheck } from '../../../../../../../../../../../baseComponents/PermissionChecker/function'
import { SetEditDeleteModalTy } from '../../../../../../../../../../../utils/hooks/useEditDeleteModal'
import useIsLoading from '../../../../../../../../../../../utils/hooks/useIsLoading'
import LoadingSpinner from '../../../../../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import { parseOrderTableData } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderQuoteLayout/function'
import { CalculateParamsTy } from '../../../../../../../../../../../baseComponents/Displays/DisplayPrice/types'
import { HeaderDataCalculate } from '@teinor/erp/types/company/order/general'
import { OrderTableData } from '@teinor/erp/prices/quote/totals'
import { TwinDictionary } from '../../../../../../../../../../../utils/globals/dictionary'
import './modalProcessSellOrder.sass'

interface ModalProcessSellOrderProps extends ModalProcessSellOrderLogicProps { 
    getCompleteData: (inputtable?: TableData) => Promise<void>
    setOpenModal: SetEditDeleteModalTy
}

const ModalProcessSellOrder: React.FC<ModalProcessSellOrderProps> = ({ setOpenModal, data, getCompleteData }) => {
    const { decideComponent, selectedProducts } = useModalProcessSellOrderLogic({ data })
    const Component = decideComponent()
    return (
        <ModalBigCentered opened={true} setOpened={() => setOpenModal(null)} onClickOut={false} className={'flex modal_process_order'}>
            <div className='flex flex-col modal_process_rightside'>
                <Component {...{ setOpenModal, data, getCompleteData, selectedProducts }} />
                <ButtonSecondary onClick={() => setOpenModal(null)} className='w-100 ml-auto mt-30 regular14'>
                    <TwinTrans transKey='back'>Volver</TwinTrans>
                </ButtonSecondary>
            </div>
        </ModalBigCentered>
    )
}

interface ModalProcessSellOrderLogicProps { 
    data: OrderData
}

const useModalProcessSellOrderLogic = ({ data }: ModalProcessSellOrderLogicProps) => {
    const selectedProducts = useMemo(() => parseSelectedTableForProcessing(data), [data])

    const decideComponent = useCallback(() => {
        const mustCancel = selectedProducts.canceling.products.length && !selectedProducts.processing.products.length
        if (mustCancel) {
            return CancelSellOrderProducts
        }
        return ProcessSellOrder
    }, [selectedProducts])

    return { decideComponent, selectedProducts }
}

interface CancelSellOrderProductsProps extends CancelOrFinishSellOrderProductsLogicProps { }

const CancelSellOrderProducts: React.FC<CancelSellOrderProductsProps> = ({ setOpenModal, data, getCompleteData, selectedProducts }) => {
    const { t } = useTwinTranslation()
    const { finalizeOrder } = useCancelOrFinishSellOrderProductsLogic({ setOpenModal, data, getCompleteData, selectedProducts })
    return (
         <div className='finish_order'>
            <h4 className='text-start'><TwinTrans transKey='cancelSellOrderProductSelectedProducts'>Cancelar todos los productos seleccionados</TwinTrans></h4>
            <div className='flex flex-col gap-y-15 mt-20'>
                <div className='flex gap-20 flex-auto w-200 m-auto'>
                    <ProcessButton finalizeOrder={finalizeOrder} url='/api/app/order/sell/sellOrder/orderProduct/cancelProcessedSellOrderProducts' navigateTo={''} text={t('cancelSellOrderProduct', 'Cancelar productos')} icon={faFileLines}/>
                </div>
            </div>
        </div>
    )
}

interface CancelOrFinishSellOrderProductsLogicProps {
    setOpenModal: SetEditDeleteModalTy
    data: OrderData
    getCompleteData: (inputtable?: TableData) => Promise<void>
    selectedProducts: SelectedTableForProcessingTy
}

const useCancelOrFinishSellOrderProductsLogic = ({ setOpenModal, data, getCompleteData, selectedProducts }: CancelOrFinishSellOrderProductsLogicProps) => {
    const finalizeOrder = useCallback(async (url: string) => {
        const response = await twinFetchPost(url, { OrderId: data?.header?.id, orderProductIds: selectedProducts.forSend.products })
        const responseJSON = await response.json()
        if (response.status === 200) {
            await getCompleteData()
        } else {
            changeErrorMessage(responseJSON.errors[0].msg)
        }
        setOpenModal(null)
    }, [setOpenModal, getCompleteData, selectedProducts, data])
    
    return { finalizeOrder }
}

interface ProcessSellOrderProps extends ProcessSellOrderLogicProps {}

const ProcessSellOrder: React.FC<ProcessSellOrderProps> = ({ selectedProducts, setOpenModal, data }) => {
    const { t } = useTwinTranslation()
    const { finalizeOrder, messageDeliveryNote, messageInvoice, loading } = useProcessSellOrderLogic({ selectedProducts, setOpenModal, data })
    const canProcess = selectedProducts.forSend.units > 0 ? true : false
    let messageError = null
    if (!canProcess) {
        messageError = t('mustAddQuantityToSelectedProducts', 'Debes añadir a los productos seleccionados una cantidad superior a 0')
    }

    return (
        <div className='process_order'>
            <h4 className='text-start'><TwinTrans transKey='processOrderFromSelectedProducts'>Procesar pedido a partir de los productos seleccionados</TwinTrans></h4>
            <div className='flex flex-col gap-y-15 mt-20'>
                {loading ? <LoadingSpinner /> :
                    <div className='flex gap-20 flex-auto'>
                        <ProcessButton finalizeOrder={finalizeOrder} url='/api/app/order/sell/sellOrder/createSellDeliveryNoteBySellOrder' navigateTo={locations.sellDeliveryNoteSingle} text={t('createDeliveryNote', 'Crear albarán')} icon={faFileLines} messageError={messageError || messageDeliveryNote || ''} />
                        <ProcessButton finalizeOrder={finalizeOrder} url='/api/app/order/sell/sellOrder/createSellInvoiceBySellOrder' navigateTo={locations.sellInvoiceSingle} text={t('createInvoice', 'Crear factura')} icon={faFileInvoice} messageError={messageError || messageInvoice || ''} />
                    </div>
                }
            </div>
        </div>
    )
}

interface ProcessSellOrderLogicProps {
    selectedProducts: SelectedTableForProcessingTy
    setOpenModal: SetEditDeleteModalTy
    data: OrderData
}

const useProcessSellOrderLogic = ({ selectedProducts, setOpenModal, data }: ProcessSellOrderLogicProps) => {
    const navigate = useNavigate()
    const { t } = useTwinTranslation()
    const messageDeliveryNote = checkSellDeliveryNoteFromOrderAllOk(data, t)
    const messageInvoice = checkSellInvoiceFromOrderAllOk(data, t)
    const errorMsg = t('noProductStockOnLine', 'No hay stock del producto en la línea')
    const noProductsMsg = t('notProcessedAnyProducts', 'No has procesado ningún producto')
    const { startLoading, endLoading, loading } = useIsLoading()

    const finalizeOrder = useCallback(async (url: string, navigateUrl: string, extraProps?: TwinDictionary) => {
        startLoading?.()
        const extraSelectedData: TwinDictionary = {}
        if(data.header.WorkSheetId){
            const copyTableData = JSON.parse(JSON.stringify(data.tableData))
            for (const productId of selectedProducts.forSend.products) {
                const myProduct = copyTableData[data.dictPosTableData[productId]]
                if (data?.extraSelectedData?.[myProduct.id]){
                    let totalProduct = 0
                    for (const selectedId in data?.extraSelectedData?.[myProduct.id]) {
                        totalProduct += parseFloat(String(data?.extraSelectedData?.[myProduct.id][selectedId]))
                    }
                    if(totalProduct === myProduct.quantity){
                        extraSelectedData[myProduct.id] = data?.extraSelectedData?.[myProduct.id]
                        continue
                    }
                }
                if (myProduct.WorkSheetStatusParsed){
                    for (const status of myProduct.WorkSheetStatusParsed) {
                        let alreadySelected = 0
                        if (data?.extraSelectedData?.[myProduct.id]?.[status?.id]){
                            alreadySelected = parseFloat(String(data?.extraSelectedData?.[myProduct.id]?.[status?.id]))
                        }
                        const diffStatusQuantity = parseFloat(status.quantity) - (parseFloat(status.alreadyOrderProcessed) + alreadySelected)
                        if (diffStatusQuantity >= myProduct?.quantity) {
                            if (myProduct?.quantity) {
                                extraSelectedData[myProduct.id] = { ...extraSelectedData[myProduct.id], [status.id]: myProduct?.quantity }
                                myProduct.quantity = 0
                            }
                        } else if (diffStatusQuantity > 0) {
                            extraSelectedData[myProduct.id] = { ...extraSelectedData[myProduct.id], [status.id]: (diffStatusQuantity + alreadySelected) }
                            myProduct.quantity = (myProduct?.quantity || 0) - (diffStatusQuantity + alreadySelected)
                        }

                    }
                }
                if (myProduct.WorkSheetProductStatusId){
                    extraSelectedData[myProduct.id] = { [myProduct?.['UniquePKSellProduct.WorkSheetProductId']]: myProduct?.quantity }
                }
            }
        }
        const response = await twinFetchPost(url, { selectedProducts: selectedProducts.forSend.products, OrderId: data?.header?.id, WorkingCenterId: data?.header?.WorkingCenterId, extraSelectedData, ...extraProps })
        const responseJSON = await response.json()
        if (response.status === 200) {
            navigate(navigateUrl + '/' + responseJSON.id)
            setOpenModal(null)
        } else {
            if (responseJSON.errors[0].msg) {
                if (responseJSON.errors[0].msg === 'noProducts') {
                    changeErrorMessage(noProductsMsg)
                    setOpenModal(null)
                } else if (responseJSON.errors[0].msg ==='Not payment method') {
                    const calculateParams = getPreparePricesConfigParam() as CalculateParamsTy
                    const headerDataCalculate: HeaderDataCalculate = {
                        OrderCosts: data.header?.OrderCosts,
                        ParamsRoundingDecimals: calculateParams,
                        OrderShipping: data.header?.OrderShipping,
                        isFinished: data.header?.isFinished,
                        OrderAccounts: data.header?.OrderAccounts
                    }
                    let selectedTableForCalculate = data.tableData
                    if(data?.selectedTableData){
                        selectedTableForCalculate = []
                        for (const element of data?.selectedTableData) {
                            selectedTableForCalculate.push(data.tableData[data.dictPosTableData[element.id]])
                        }
                    }
                    const totalsOrderTableData = parseOrderTableData(selectedTableForCalculate as OrderTableData, headerDataCalculate)
                    setOpenModal({ type: 'delete', allRowData: { extraProps: { OrderId: data?.header?.id, WorkingCenterId: data?.header?.WorkingCenterId, selectedProducts: selectedProducts.forSend.products }, createUrl: url, navigateUrl, total: totalsOrderTableData.totals.total, finalizeOrder } })
                }else if (responseJSON.errors[0].msg.includes('noStock')) {
                    const skipStock = permissionCheck(getUserPermissions('sellOther.sellWithoutStock'), 'read')
                    if (skipStock) {
                        setOpenModal({ type: 'edit', allRowData: { extraProps: { OrderId: data?.header?.id, selectedProducts: selectedProducts.forSend.products, WorkingCenterId: data?.header?.WorkingCenterId }, createUrl: url, navigateUrl, data, finalizeOrder } })
                    } else {
                        const msgArr = responseJSON.errors[0].msg.split('-')
                        let myMsg = errorMsg + ' ' + msgArr[1]
                        if (msgArr[2]) {
                            myMsg += ' nº: ' + msgArr[2]
                        }
                        changeErrorMessage(myMsg)
                        setOpenModal(null)
                    }
                } else {
                    changeErrorMessage(responseJSON.errors[0].msg)
                    setOpenModal(null)
                }
            }
        }
        endLoading?.()
    }, [selectedProducts, navigate, setOpenModal, data, errorMsg, noProductsMsg, startLoading, endLoading])

    return { finalizeOrder, messageDeliveryNote, messageInvoice, setOpenModal, loading }
}

export default ModalProcessSellOrder